"use client";
import { FC, useEffect, useState } from "react";
import { isPreviewing, isEditing } from "@builder.io/sdk-react";
import {
  isValidManualData,
  isValidRating,
  type ResolvedRating,
  type ManualRatingData,
} from "./G2ProductRating.validation";
import { Rating } from "@/components/G2Rating";
import Image from "next/image";

export type G2RatingBlockProps = {
  logo?: string;
  errorText: string;
  errorFallbackUrl: string;
  resolvedRating?: ResolvedRating | null;
  g2ProductId?: string;
  manualData?: ManualRatingData;
  onG2Text?: string;
  dld?: string;
  dlt?: string;
  errorDld?: string;
  errorDlt?: string;
};

export const G2RatingBlock: FC<G2RatingBlockProps> = ({
  logo,
  errorText,
  errorFallbackUrl,
  resolvedRating: serverResolvedRating,
  g2ProductId,
  manualData,
  dld,
  dlt,
  errorDld,
  errorDlt,
  onG2Text,
}) => {
  const [resolvedRating, setResolvedRating] = useState<ResolvedRating | null>(
    serverResolvedRating ?? null,
  );
  const isPreview = isPreviewing() || isEditing();

  useEffect(() => {
    if (!isPreview) return;

    let isMounted = true;

    const fetchRating = async () => {
      try {
        if (g2ProductId) {
          const response = await fetch("/api/builder/g2-product", {
            method: "POST",
            body: JSON.stringify({ g2ProductId }),
          });

          if (!response.ok) throw new Error("Failed to fetch G2 product");
          const { product } = await response.json();

          if (product && isMounted) {
            const { star_rating: ratingValue, g2_url: reviewUrl } =
              product.attributes;

            if (
              isValidRating(ratingValue) &&
              typeof reviewUrl === "string" &&
              reviewUrl.length > 0
            ) {
              setResolvedRating({ ratingValue, reviewUrl });
              return;
            }
          }
        }

        if (isMounted && isValidManualData(manualData)) {
          setResolvedRating({
            ratingValue: manualData.ratingValue,
            reviewUrl: manualData.reviewUrl,
          });
          return;
        }

        if (isMounted) {
          setResolvedRating(serverResolvedRating ?? null);
        }
      } catch {
        if (isMounted && isValidManualData(manualData)) {
          setResolvedRating({
            ratingValue: manualData.ratingValue,
            reviewUrl: manualData.reviewUrl,
          });
        } else if (isMounted) {
          setResolvedRating(serverResolvedRating ?? null);
        }
      }
    };

    fetchRating();

    return () => {
      isMounted = false;
    };
  }, [isPreview, g2ProductId, manualData, serverResolvedRating]);

  const rating = isPreview ? resolvedRating : serverResolvedRating;

  if (rating) {
    return (
      <a
        href={rating.reviewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-linkdetail={dld}
        data-linktype={dlt}
      >
        <Rating rating={rating.ratingValue} logo={logo} onG2Text={onG2Text} />
      </a>
    );
  }

  return (
    <div className="flex flex-row items-center gap-3">
      {logo && (
        <Image
          src={logo}
          alt="logo"
          width={24}
          height={24}
          className="h-6 w-6 object-contain"
        />
      )}
      <a
        href={errorFallbackUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-body-secondary text-body-xs font-normal"
        data-linkdetail={errorDld}
        data-linktype={errorDlt}
      >
        {errorText}
      </a>
    </div>
  );
};
